import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Files, Clock, CheckCircle, ShieldCheck, ArrowRight, Warning, Sparkle, Info
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import StatusTimeline from '../components/StatusTimeline';
import { SearchReportInline } from '../components/SearchReportCard';
import { OnboardingInline } from '../components/OnboardingCard';
import { OfficeActionInline } from '../components/OfficeActionCard';
import { chipTextColor, needsAttention as stageNeedsAttention, resolveStageId, stageOrdinal, isRegistered, isClosed, phaseSummary } from '../lib/stageModel';
import MatterCard from '../components/MatterCard';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import API_URL from '../config';

const STATUS_CONFIG = {
  onboarding: { label: 'Onboarding', class: 'status-onboarding' },
  filed: { label: 'Filed', class: 'status-filed' },  office_action: { label: 'Office Action Received', class: 'status-drafting' },
  office_action_cleared: { label: 'OA Cleared', class: 'status-search' },
  published: { label: 'Published', class: 'status-pending' },
  opposition_cleared: { label: 'Opposition Cleared', class: 'status-onboarding' },
  registered: { label: 'Registered', class: 'status-registered' },
  dead: { label: 'Dead', class: 'status-dead' }
};

// Per-matter journey chip (spec §2.0.6). Falls back to the legacy status label.
const JOURNEY_CHIP = {
  PRE_SEARCH: 'Onboarding',
  SEARCH_READY: 'Search complete',
  PREP_FILING: 'Preparing your application',
  FILED: 'Filed with the USPTO',
  PAUSED: 'On hold',
};

function getPhaseChipLabel(matter) {
  const jp = matter.journey_phase;
  if (jp === 'CLOSED') {
    return (matter.status_phase === 'registered' || matter.registration_number) ? 'Registered' : 'Closed';
  }
  if (jp && JOURNEY_CHIP[jp]) return JOURNEY_CHIP[jp];
  return STATUS_CONFIG[matter.status_phase]?.label || matter.status_phase;
}

const FILED_STATUS = ['filed', 'office_action', 'office_action_cleared', 'published', 'opposition_cleared', 'registered'];
const FILED_PENDING_STATUS = ['filed', 'office_action', 'office_action_cleared', 'published', 'opposition_cleared'];
function isOnboardingStage(m) {
  if (FILED_STATUS.includes(m.status_phase)) return false;
  if (['SEARCH_READY', 'PREP_FILING'].includes(m.journey_phase)) return false;
  return m.journey_phase === 'PRE_SEARCH' || (!m.journey_phase && m.status_phase === 'onboarding');
}
function isSearchCompletedStage(m) {
  if (FILED_STATUS.includes(m.status_phase)) return false;
  return ['SEARCH_READY', 'PREP_FILING'].includes(m.journey_phase);
}
function isPrefilingStage(m) {
  if (FILED_STATUS.includes(m.status_phase)) return false;
  if (['dead', 'abandoned'].includes(m.status_phase)) return false;
  return ['PRE_SEARCH', 'SEARCH_READY', 'PREP_FILING'].includes(m.journey_phase) || m.status_phase === 'onboarding';
}
function isClosedStage(m) {
  return ['dead', 'abandoned'].includes(m.status_phase);
}
// Returns the soonest upcoming §8/§9 maintenance date if a renewal is "approaching"
// (window already open, or due within the next 12 months) — else null.
function renewalDueDate(m) {
  const now = new Date();
  const soon = new Date();
  soon.setMonth(soon.getMonth() + 12);
  let best = null;
  for (const d of (m.uspto_deadlines || [])) {
    if (!isMaintenanceDeadline(d)) continue;
    const act = d.actionable_date ? new Date(d.actionable_date) : null;
    const wo = d.window_opens ? new Date(d.window_opens) : null;
    const windowOpen = wo && wo <= now && (!act || act >= now);
    const dueSoon = act && act >= now && act <= soon;
    if (windowOpen || dueSoon) {
      const dt = act || wo;
      if (!best || dt < best) best = dt;
    }
  }
  return best;
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  // Handle various date formats
  const date = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function isLogoMark(markDrawingType) {
  if (!markDrawingType) return false;
  const type = markDrawingType.toUpperCase();
  return type !== 'STANDARD_CHARACTERS' && type !== 'TYPED_DRAWING' && type !== '';
}

const URGENCY_STYLES = {
  overdue: { bg: '#fef2f2', border: '#fca5a5', text: '#b91c1c', label: 'Overdue' },
  urgent:  { bg: '#fef2f2', border: '#fca5a5', text: '#b91c1c', label: 'Urgent' },
  soon:    { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', label: 'Upcoming' },
  normal:  { bg: '#f0fdf4', border: '#86efac', text: '#166534', label: 'On track' },
};

function formatCountdown(days) {
  if (days == null) return '';
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`;
  if (days === 0) return 'today';
  if (days < 30) return `in ${days} day${days === 1 ? '' : 's'}`;
  if (days < 365) {
    const m = Math.round(days / 30);
    return `in ${m} month${m === 1 ? '' : 's'}`;
  }
  const y = (days / 365).toFixed(1).replace(/\.0$/, '');
  return `in ${y} year${y === '1' ? '' : 's'}`;
}

const isMaintenanceDeadline = (d) =>
  !!d?.stage && !!d?.window_opens && !!d?.actionable_date;

function collectDeadlines(matters) {
  // Flatten every PRE-REGISTRATION deadline (Office Action, Statement of Use, Opposition window, etc.)
  // across every matter. Maintenance deadlines (§8 / §9 / 10-year renewals) are explicitly EXCLUDED
  // here — those are a Protection Plan benefit and live only on the Protection Plan page.
  const all = [];
  for (const m of matters) {
    for (const d of (m.uspto_deadlines || [])) {
      if (isMaintenanceDeadline(d)) continue;
      all.push({ ...d, matter_id: m.id, trademark_name: m.trademark_name });
    }
  }
  const order = { overdue: 0, urgent: 1, soon: 2, normal: 3 };
  all.sort((a, b) => {
    const u = (order[a.urgency] ?? 99) - (order[b.urgency] ?? 99);
    if (u !== 0) return u;
    return (a.due_date || '').localeCompare(b.due_date || '');
  });
  return all;
}


export default function ClientDashboard() {
  const { user } = useAuth();
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [protectionPlan, setProtectionPlan] = useState(null);
  const [monitorAlerts, setMonitorAlerts] = useState([]);
  const [showAllDeadlines, setShowAllDeadlines] = useState(false);
  const [filter, setFilter] = useState('all');

  const markReportViewed = (matter) => {
    axios.post(`${API_URL}/api/client/matters/${matter.id}/report-viewed`).catch(() => {});
  };

  const markReportSeen = (matter) => {
    if (matter.search_report_viewed_at) return;
    // Optimistically clear the 'New' marker for the session, then persist.
    setMatters((prev) => prev.map((m) => m.id === matter.id ? { ...m, search_report_viewed_at: new Date().toISOString() } : m));
    axios.post(`${API_URL}/api/client/matters/${matter.id}/report-seen`).catch(() => {});
  };

  const submitDecision = async (matter, decision) => {
    // Optimistic swap so the confirmation shows instantly.
    setMatters((prev) => prev.map((m) => m.id === matter.id ? { ...m, client_decision: decision, client_decision_at: new Date().toISOString() } : m));
    try {
      const res = await axios.post(`${API_URL}/api/client/matters/${matter.id}/decision`, { decision });
      setMatters((prev) => prev.map((m) => m.id === matter.id ? res.data : m));
    } catch (e) {
      toast.error('Could not save your choice — please try again.');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [mattersRes, planRes] = await Promise.all([
        axios.get(`${API_URL}/api/client/matters`),
        axios.get(`${API_URL}/api/client/protection-plan`).catch(() => ({ data: { has_protection_plan: false } }))
      ]);
      setMatters(mattersRes.data);
      setProtectionPlan(planRes.data);
      axios.get(`${API_URL}/api/client/monitoring/alerts`)
        .then((r) => setMonitorAlerts(r.data?.needs_look || []))
        .catch(() => {});
    } catch (error) {
      toast.error('Failed to load matters');
    } finally {
      setLoading(false);
    }
  };

  const registeredCount = matters.filter(m => m.status_phase === 'registered').length;
  const CATS = {
    needsAttention: stageNeedsAttention,
    prefiling: isPrefilingStage,
    filedPending: m => FILED_PENDING_STATUS.includes(m.status_phase),
    registered: m => m.status_phase === 'registered',
    closed: isClosedStage,
  };
  const counts = {
    all: matters.length,
    needsAttention: matters.filter(CATS.needsAttention).length,
    prefiling: matters.filter(CATS.prefiling).length,
    filedPending: matters.filter(CATS.filedPending).length,
    registered: registeredCount,
    closed: matters.filter(CATS.closed).length,
  };
  const filterCards = [
    { key: 'all', label: 'All Matters', count: counts.all },
    { key: 'needsAttention', label: 'Needs Your Attention', count: counts.needsAttention, accent: true },
    { key: 'prefiling', label: 'Pre-filing (Search & Prep)', count: counts.prefiling },
    { key: 'filedPending', label: 'Filed & Pending', count: counts.filedPending },
    { key: 'registered', label: 'Registered', count: counts.registered },
  ];
  if (counts.closed > 0) filterCards.push({ key: 'closed', label: 'Closed / Abandoned', count: counts.closed });
  const activeFilter = CATS[filter] ? filter : 'all';
  const sortByUrgency = (arr) => [...arr].sort((a, b) => {
    const na = stageNeedsAttention(a) ? 1 : 0;
    const nb = stageNeedsAttention(b) ? 1 : 0;
    if (na !== nb) return nb - na;
    return stageOrdinal(resolveStageId(b)) - stageOrdinal(resolveStageId(a));
  });
  const filteredMatters = sortByUrgency(activeFilter === 'all' ? matters : matters.filter(CATS[activeFilter]));
  const firstReportId = filteredMatters.find((m) => m.search_report_url)?.id;
  const firstUndecidedReportId = filteredMatters.find((m) => m.search_report_url && !m.client_decision)?.id;
  const activeMatters = filteredMatters.filter((m) => !isRegistered(m) && !isClosed(m));
  const registeredMatters = filteredMatters.filter((m) => isRegistered(m) || isClosed(m));
  const singleMatter = matters.length === 1;
  const autoExpand = (m) => singleMatter || resolveStageId(m) === '3779796689' || m.id === firstUndecidedReportId;
  const hasPP = !!protectionPlan?.has_protection_plan;

  const allDeadlines = collectDeadlines(matters);
  const visibleDeadlines = showAllDeadlines ? allDeadlines : allDeadlines.slice(0, 3);

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#ffd100] border-t-transparent rounded-full animate-spin"></div></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="client-dashboard">
        {/* Header */}
        <div>
          <p className="label-uppercase mb-1" style={{ color: '#1d57a5' }}>Overview</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#141b4d' }}>Your Matters</h1>
        </div>

        {/* Quarterly monitoring — "needs a look" alerts (impossible to miss) */}
        {monitorAlerts.length > 0 && (
          <div className="card-swiss border-l-4" style={{ borderColor: '#DC2626', background: '#FEF2F2' }}
            data-testid="dashboard-monitoring-alerts">
            <div className="flex items-center gap-2 mb-3">
              <Warning weight="fill" className="w-5 h-5" style={{ color: '#DC2626' }} />
              <h2 className="text-lg font-bold" style={{ color: '#09090B' }}>
                Monitoring found {monitorAlerts.length} filing{monitorAlerts.length > 1 ? 's' : ''} that need{monitorAlerts.length === 1 ? 's' : ''} a look
              </h2>
            </div>
            <div className="space-y-3">
              {monitorAlerts.map((f, i) => (
                <div key={i} className="flex items-start justify-between gap-4 flex-wrap bg-white border border-[#FECACA] p-3">
                  <div className="min-w-0">
                    <p className="font-bold" style={{ color: '#09090B' }}>{f.filing}
                      {f.owner && <span className="font-normal" style={{ color: '#52525B' }}> · {f.owner}</span>}</p>
                    {f.goods && <p className="text-sm" style={{ color: '#101820' }}>{f.goods}</p>}
                    {f.close_to?.length > 0 && (
                      <p className="text-sm mt-0.5" style={{ color: '#101820' }}>Close to: <strong>{f.close_to.join(', ')}</strong></p>
                    )}
                    {f.recommendation && <p className="text-sm mt-1 font-medium" style={{ color: '#141b4d' }}>Recommendation: {f.recommendation}</p>}
                    {f.days_left !== null && f.days_left !== undefined && (
                      <p className="text-xs font-bold mt-1" style={{ color: f.days_left <= 21 ? '#DC2626' : '#52525B' }}>
                        Opposition deadline: {f.days_left < 0 ? `${Math.abs(f.days_left)} days ago` : f.days_left === 0 ? 'today' : `${f.days_left} days left`}
                      </p>
                    )}
                  </div>
                  <a href="https://meetings-na2.hubspot.com/joey-vitale/protection-plan-call" target="_blank" rel="noopener noreferrer"
                    data-testid="dashboard-monitoring-book-call"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold flex-shrink-0 transition-transform hover:-translate-y-0.5"
                    style={{ background: '#141b4d', color: '#fff' }}>
                    Book a call
                  </a>
                </div>
              ))}
            </div>
            <Link to="/protection-plan" className="inline-block mt-3 text-sm font-semibold" style={{ color: '#1d57a5' }}
              data-testid="dashboard-monitoring-viewall">
              View full quarterly monitoring →
            </Link>
          </div>
        )}

        {/* Single matter → one-line summary; multiple → filter tiles */}
        {matters.length === 1 ? (
          <p className="text-base" data-testid="single-matter-line" style={{ color: '#101820' }}>
            Your trademark <span className="font-semibold" style={{ color: '#141b4d' }}>{matters[0].trademark_name}</span>{' '}
            {phaseSummary(matters[0]).verb}{' '}
            <span className="font-semibold" style={{ color: '#141b4d' }}>{phaseSummary(matters[0]).noun}</span>.
          </p>
        ) : matters.length > 1 ? (
        <div>
          <p className="text-sm font-bold uppercase tracking-wide mb-2" style={{ color: '#9a9488' }}>Filter:</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="matter-filters">
          {filterCards.map((c) => {
            const active = activeFilter === c.key;
            const isAccent = c.accent && c.count > 0;
            const bg = active ? (isAccent ? '#c2410c' : '#141b4d') : '#ffffff';
            const border = active ? bg : (isAccent ? '#f0c9a8' : '#ece5d8');
            const labelColor = active ? '#ffffff' : (isAccent ? '#c2410c' : '#9a9488');
            const numColor = active ? '#ffffff' : '#141b4d';
            return (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                data-testid={`filter-${c.key}`}
                aria-pressed={active}
                className="text-left p-4 transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: bg, border: `1px solid ${border}` }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  {isAccent && (
                    <Warning weight="fill" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: active ? '#ffffff' : '#c2410c' }} />
                  )}
                  <span className="text-[11px] font-bold uppercase tracking-wide leading-tight" style={{ color: labelColor }}>
                    {c.label}
                  </span>
                </div>
                <p className="text-2xl font-bold" style={{ color: numColor }}>{c.count}</p>
              </button>
            );
          })}
          </div>
        </div>
        ) : null}

        {/* Aggregated Upcoming Deadlines */}
        {allDeadlines.length > 0 && (
          <div className="card-swiss" data-testid="upcoming-deadlines-card">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <p className="label-uppercase mb-1" style={{ color: '#1d57a5' }}>What's Due Next</p>
                <h2 className="text-xl font-semibold" style={{ color: '#141b4d' }}>
                  Upcoming Deadlines <span className="text-base font-normal" style={{ color: '#9a9488' }}>({allDeadlines.length})</span>
                </h2>
              </div>
            </div>
            <div className="space-y-3">
              {visibleDeadlines.map((d, idx) => {
                const style = URGENCY_STYLES[d.urgency] || URGENCY_STYLES.normal;
                const isUrgent = d.urgency === 'overdue' || d.urgency === 'urgent';
                const isMaintenance = isMaintenanceDeadline(d);
                const displayDate = isMaintenance ? d.actionable_date : d.due_date;
                const displayDays = isMaintenance ? d.days_to_actionable : d.days_remaining;
                const chipLabel = isMaintenance ? d.actionable_label : style.label;
                return (
                  <Link
                    key={`${d.matter_id}-${idx}`}
                    to={`/matters/${d.matter_id}`}
                    className="block p-4 hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: style.bg, border: `1px solid ${style.border}` }}
                    data-testid={`dashboard-deadline-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      {isUrgent ? (
                        <Warning weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: style.text }} />
                      ) : (
                        <Clock weight="duotone" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: style.text }} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div>
                            <p className="font-semibold" style={{ color: style.text }}>{d.label}</p>
                            <p className="text-sm mt-0.5" style={{ color: '#52525B' }}>
                              {d.trademark_name}
                            </p>
                          </div>
                          <span className="text-xs font-medium px-2 py-0.5" style={{ backgroundColor: style.text, color: style.bg }}>
                            {chipLabel}
                          </span>
                        </div>
                        <p className="text-sm mt-2" style={{ color: style.text }}>
                          <span className="font-medium">{formatDate(displayDate)}</span>
                          <span className="mx-2">·</span>
                          {isMaintenance
                            ? `${d.actionable_label} ${formatCountdown(displayDays)}`
                            : formatCountdown(displayDays)}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            {allDeadlines.length > 3 && (
              <button
                onClick={() => setShowAllDeadlines(!showAllDeadlines)}
                className="mt-4 text-sm font-medium inline-flex items-center gap-1 hover:underline"
                style={{ color: '#1d57a5' }}
                data-testid="toggle-all-deadlines"
              >
                {showAllDeadlines ? 'Show fewer' : `View all ${allDeadlines.length} deadlines`}
                <ArrowRight weight="bold" className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Matters List — Active, with a collapsible Registered group at the bottom */}
        <div>
          {matters.length === 0 ? (
            <div className="card-swiss text-center py-12" data-testid="no-matters-message">
              <Files weight="duotone" className="w-12 h-12 mx-auto mb-4" style={{ color: '#9a9488' }} />
              <h3 className="text-lg font-medium mb-2" style={{ color: '#141b4d' }}>We're still connecting your records</h3>
              <p style={{ color: '#9a9488' }}>Your matters will appear here shortly. If they haven't shown up within a day, just reply to any email from us and we'll sort it out.</p>
            </div>
          ) : filteredMatters.length === 0 ? (
            <div className="card-swiss text-center py-10" data-testid="no-filtered-matters">
              <p style={{ color: '#9a9488' }}>No matters in this view.</p>
              <button onClick={() => setFilter('all')} className="mt-3 text-sm font-semibold hover:underline" style={{ color: '#1d57a5' }} data-testid="clear-filter-btn">Show all matters</button>
            </div>
          ) : (
            <>
              {activeMatters.length > 0 && (
                <>
                  <h2 className="text-xl font-semibold mb-4" style={{ color: '#141b4d' }}>Active</h2>
                  <div className="space-y-4">
                    {activeMatters.map((m) => (
                      <MatterCard key={m.id} matter={m} isFirstReport={m.id === firstReportId}
                        defaultExpanded={autoExpand(m)} hasProtectionPlan={hasPP}
                        onViewReport={() => markReportViewed(m)}
                        onReportSeen={markReportSeen} onDecision={submitDecision} />
                    ))}
                  </div>
                </>
              )}

              {registeredMatters.length > 0 && (
                <div className={activeMatters.length > 0 ? 'mt-8' : ''}>
                  <h2 className="text-xl font-semibold mb-4" style={{ color: '#141b4d' }} data-testid="registered-group-heading">
                    Registered ({registeredMatters.length})
                  </h2>
                  <div className="space-y-4">
                    {registeredMatters.map((m) => (
                      <MatterCard key={m.id} matter={m} isFirstReport={m.id === firstReportId}
                        defaultExpanded={singleMatter} hasProtectionPlan={hasPP}
                        onViewReport={() => markReportViewed(m)}
                        onReportSeen={markReportSeen} onDecision={submitDecision} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Missing-trademark self-report callout (helps surface split/duplicate-contact cases) */}
        {matters.length > 0 && (
          <div
            className="card-swiss"
            style={{ backgroundColor: '#f2e9db', borderColor: '#e4d9c4' }}
            data-testid="missing-marks-callout"
          >
            <div className="flex items-start gap-3">
              <Info weight="duotone" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#1d57a5' }} />
              <div>
                <p className="font-medium mb-1" style={{ color: '#141b4d' }}>Not seeing all your trademarks?</p>
                <p className="text-sm leading-relaxed" style={{ color: '#3f3f46' }}>
                  Sometimes we have more than one email address on file for you, which can split your marks
                  across accounts. Email us at{' '}
                  <a href="mailto:clients@indielaw.com?subject=Missing%20trademarks%20on%20my%20portal"
                    className="font-medium hover:underline" style={{ color: '#1d57a5' }}
                    data-testid="missing-marks-mailto">
                    clients@indielaw.com
                  </a>{' '}
                  — include any other emails you've used with us — and we'll merge everything so it all shows up here.
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
